import React, {useCallback, useEffect, useState} from 'react';
import SecurSpaceContainer from "../../../components/common/SecurSpaceContainer";
import {Grid, Typography} from "@mui/material";
import moment from "moment";
import {DateFormats} from "../../../components/constants/securspace-constants";
import InvoiceList from "../../../components/admin/invoices/redesign/InvoiceList";
import InvoiceSortFilter from "../../../components/admin/invoices/redesign/InvoiceSortFilter/InvoiceSortFilter";
import InvoiceActionsDialogWrapper
  from "../../../components/admin/invoices/redesign/InvoiceDialogs/InvoiceActionsDialogWrapper";
import Pagination from "../../../components/deprecatedUikit/Pagination/Pagination";
import {requestAdminInvoices} from "../../../requests/reports/invoices/admin-invoice-requests";
import InvoiceGroupSummary from "../../../components/admin/invoices/redesign/InvoiceGroupSummary";
import ExportCSVs from "../../../components/admin/invoices/redesign/ExportCSVs";
import {getErrorMessageForNonStandardAndStandardResponse} from "../../../util/NetworkErrorUtil";
import {CallbackActions} from "../../../util/ActionUtils";
import useUrlQueryParams from "../../../hooks/useUrlQueryParams";
import {useSnackbar} from "../../../context/snackbar-context";
import {SecurSpaceReportContainer} from "../../../components/common";


/**
 * This component will be refactored/replaced to use bound url query params. No need to migrate to TS.
 * @param account
 * @return {React.JSX.Element}
 */
const AdminInvoicesReport = ({account}) => {

  const { snackbarShowMessage } = useSnackbar();
  const urlParams = useUrlQueryParams();
  const urlBookingNumber = urlParams.bookingNumber || "";
  const urlInvoiceNumber = urlParams.invoiceNumber || "";

  const defaultQuery = {
    startDate: moment().subtract(1, 'month').format(DateFormats.DAY),
    endDate: moment().add(1, 'month').format(DateFormats.DAY),
    bookingNumber: urlBookingNumber,
    transactionNumber: urlInvoiceNumber,
    assetTypeGroup: '',
    buyerAccountId: '',
    supplierAccountId: '',
    transactionType: '',
    bookingStatus: '',
    supplierTransactionStatus: '',
    taxStatus: '',
    paymentProcessor: '',
    sortBy: 'TRANSACTION_NUMBER',
    sortDir: 'desc',
    page: 0,
    size: 10,
  };

  const [query, setQuery] = useState({...defaultQuery});
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [totalNumberResults, setTotalNumberResults] = useState(0);
  const [page, setPage] = useState(0);
  const [activeDialog, setActiveDialog] = useState(null);
  const [activeInvoice, setActiveInvoice] = useState(null);

  const getAdminInvoices = useCallback(() => {
    setLoading(true);
    requestAdminInvoices({...query, page})
      .then((response) => {
        const {content, totalPages, count, page} = response.body;
        if (page + 1 > totalPages) {
          // If the page index is greater than the total pages
          // This will refresh the data and show the last page of results
          setPage(totalPages > 0 ? totalPages - 1 : 0);
        }
        setInvoices(content);
        setTotalNumberResults(count);
      })
      .catch((err) => {
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [query, page, snackbarShowMessage]);

  useEffect(() => {
    getAdminInvoices();
  }, [getAdminInvoices]);

  const onInvoiceAction = (callbackAction, invoice, callBackActionDetail) => {
    switch (callbackAction) {
      case CallbackActions.ERROR:
        snackbarShowMessage(callBackActionDetail, 'error');
        break;
      case CallbackActions.OPEN_DIALOG:
        setActiveInvoice(invoice);
        setActiveDialog(callBackActionDetail);
        break;
      case CallbackActions.SUCCESS:
        getAdminInvoices();
        if (typeof callBackActionDetail === 'string' && callBackActionDetail.trim()) {
          snackbarShowMessage(callBackActionDetail, 'success');
        }
        break;
      default:
        throw new Error('Invalid callback action');
    }
  }

  const onDialogClose = (...callbackActions) => {
    setActiveDialog(null);
    setActiveInvoice(null);
    if (callbackActions?.length)  {
      onInvoiceAction(...callbackActions);
    }
  }

  return (
    <SecurSpaceContainer>
      <InvoiceActionsDialogWrapper
        account={account}
        activeDialog={activeDialog}
        invoice={activeInvoice}
        onInvoiceAction={onInvoiceAction}
        onClose={onDialogClose}
      />
      <SecurSpaceReportContainer container rowGap={4}>
        <Typography variant={'h5'} component={'h1'}>Invoices</Typography>
        <InvoiceSortFilter
          initialQuery={defaultQuery}
          setQuery={setQuery}
        />
        <ExportCSVs query={query} />
        <InvoiceGroupSummary query={query} />
        <InvoiceList
          account={account}
          invoices={invoices}
          loading={loading}
          onInvoiceAction={onInvoiceAction}
        />
        <Grid item container justifyContent={'end'}>
          <Pagination
            count={totalNumberResults}
            rowsPerPage={query.size}
            page={page}
            onPageChange={(_event, page) => setPage(page)}
            onRowsPerPageChange={(event) => (setQuery((prevState) => ({...prevState, size: event.target.value})))}
            variant={'tablePagination'}
          />
        </Grid>
      </SecurSpaceReportContainer>
    </SecurSpaceContainer>
  );
};

export default AdminInvoicesReport;
